import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RecentActivity = ({ activities }) => {
  const [filter, setFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All Activity', icon: 'Activity' },
    { value: 'added', label: 'Added', icon: 'Plus' },
    { value: 'updated', label: 'Updated', icon: 'Edit' },
    { value: 'sold', label: 'Sold', icon: 'TrendingDown' },
    { value: 'valued', label: 'Valued', icon: 'DollarSign' }
  ];

  const filteredActivities = filter === 'all' 
    ? activities 
    : activities?.filter(activity => activity?.type === filter);

  const getActivityIcon = (type) => {
    const iconMap = {
      added: 'Plus',
      updated: 'Edit',
      sold: 'TrendingDown',
      valued: 'DollarSign',
      market_update: 'TrendingUp'
    };
    return iconMap?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colorMap = {
      added: 'text-success',
      updated: 'text-warning',
      sold: 'text-destructive',
      valued: 'text-primary',
      market_update: 'text-conversion-accent'
    };
    return colorMap?.[type] || 'text-muted-foreground';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">Recent Activity</h3>
        <button className="text-sm text-primary hover:text-primary/80 transition-colors duration-200">
          View All
        </button>
      </div>
      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 overflow-x-auto">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
              filter === option?.value
                ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:text-card-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={option?.icon} size={16} />
            <span>{option?.label}</span>
          </button>
        ))}
      </div>
      {/* Activity List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredActivities?.length > 0 ? (
          filteredActivities?.map((activity) => (
            <div key={activity?.id} className="flex items-start space-x-4 p-3 rounded-lg hover:bg-muted/30 transition-colors duration-200">
              {/* Activity Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-muted/50 ${getActivityColor(activity?.type)}`}>
                <Icon name={getActivityIcon(activity?.type)} size={18} />
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-card-foreground mb-1">
                      {activity?.title}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      {activity?.description}
                    </p>
                    
                    {/* Item Preview */}
                    {activity?.item && (
                      <div className="flex items-center space-x-3 p-2 bg-muted/20 rounded-lg">
                        <Image
                          src={activity?.item?.image}
                          alt={activity?.item?.name}
                          className="w-8 h-8 rounded object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-card-foreground truncate">
                            {activity?.item?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {activity?.item?.category}
                          </p>
                        </div>
                        {activity?.item?.value && (
                          <div className="text-xs font-medium text-primary">
                            ${activity?.item?.value?.toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-muted-foreground ml-4">
                    {formatTimeAgo(activity?.timestamp)}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No recent activity found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === 'all' ? 'Start adding items to see activity' : `No ${filter} activities yet`}
            </p>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="flex space-x-3 mt-6 pt-6 border-t border-border">
        <button className="flex-1 flex items-center justify-center space-x-2 py-2 px-4 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors duration-200">
          <Icon name="Plus" size={16} />
          <span className="text-sm font-medium">Add Item</span>
        </button>
        <button className="flex-1 flex items-center justify-center space-x-2 py-2 px-4 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted transition-colors duration-200">
          <Icon name="BarChart3" size={16} />
          <span className="text-sm font-medium">Analyze</span>
        </button>
      </div>
    </div>
  );
};

export default RecentActivity;